import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
} from "amazon-cognito-identity-js";
import {
  buildDevBypassUser,
  isAdminFromGroups,
  userFromIdTokenPayload,
} from "@/lib/authRoles";
import { cognitoUserPool, isCognitoConfigured, isLocalDevBypassEnabled } from "@/lib/cognitoPool";
import { setCognitoIdToken } from "@/lib/tokenStore";

const AuthContext = createContext(null);

function createCognitoUser(email) {
  return new CognitoUser({
    Username: email.trim().toLowerCase(),
    Pool: cognitoUserPool,
  });
}

/** @returns {ReturnType<typeof userFromIdTokenPayload> | null} */
function userFromSession(session) {
  if (!session?.isValid()) {
    setCognitoIdToken(null);
    return null;
  }
  const idJwt = session.getIdToken().getJwtToken();
  setCognitoIdToken(idJwt);
  return userFromIdTokenPayload(session.getIdToken().decodePayload());
}

/** Only pass mutable / newly collected attrs. Cognito rejects updating email etc. during NEW_PASSWORD_REQUIRED. */
function filterChallengeAttributes(userAttributes) {
  if (!userAttributes) return {};
  const skip = new Set([
    "sub",
    "username",
    "email",
    "email_verified",
    "phone_number",
    "phone_number_verified",
    "identities",
    "picture",
    "nickname",
    "preferred_username",
  ]);
  const out = {};
  for (const [key, value] of Object.entries(userAttributes)) {
    if (skip.has(key)) continue;
    if (value === undefined || value === null || value === "") continue;
    out[key] = value;
  }
  return out;
}

export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const pendingNewPasswordRef = useRef(null);

  const groups = user?.groups ?? [];
  const role = user?.role ?? null;
  const isAdmin = useMemo(() => isAdminFromGroups(groups), [groups]);

  const clearAuth = useCallback(() => {
    setIsAuthenticated(false);
    setUser(null);
    setCognitoIdToken(null);
  }, []);

  const refreshFromPool = useCallback(() => {
    if (isLocalDevBypassEnabled()) {
      setUser(buildDevBypassUser());
      setIsAuthenticated(true);
      setCognitoIdToken(null);
      setLoading(false);
      return;
    }
    if (!cognitoUserPool) {
      setLoading(false);
      clearAuth();
      return;
    }
    const cognitoUser = cognitoUserPool.getCurrentUser();
    if (!cognitoUser) {
      clearAuth();
      setLoading(false);
      return;
    }
    cognitoUser.getSession((err, session) => {
      if (err || !session?.isValid()) {
        clearAuth();
        setLoading(false);
        return;
      }
      setUser(userFromSession(session));
      setIsAuthenticated(true);
      setLoading(false);
    });
  }, [clearAuth]);

  useEffect(() => {
    refreshFromPool();
  }, [refreshFromPool]);

  const login = useCallback((email, password) => {
    return new Promise((resolve) => {
      if (isLocalDevBypassEnabled()) {
        setUser(buildDevBypassUser(email));
        setIsAuthenticated(true);
        setCognitoIdToken(null);
        resolve({ success: true });
        return;
      }
      if (!cognitoUserPool) {
        resolve({
          success: false,
          error: "Cognito is not configured (missing pool/client env).",
        });
        return;
      }
      const authDetails = new AuthenticationDetails({
        Username: email.trim().toLowerCase(),
        Password: password,
      });
      const cognitoUser = new CognitoUser({
        Username: email.trim().toLowerCase(),
        Pool: cognitoUserPool,
      });
      cognitoUser.authenticateUser(authDetails, {
        onSuccess: (session) => {
          setUser(userFromSession(session));
          setIsAuthenticated(true);
          resolve({ success: true });
        },
        onFailure: (err) => {
          setCognitoIdToken(null);
          resolve({
            success: false,
            error: err.message || "Sign in failed",
          });
        },
        newPasswordRequired: (userAttributes) => {
          pendingNewPasswordRef.current = {
            cognitoUser,
            userAttributes: filterChallengeAttributes(userAttributes),
          };
          resolve({ success: false, newPasswordRequired: true });
        },
      });
    });
  }, []);

  const completeNewPassword = useCallback((newPassword) => {
    return new Promise((resolve) => {
      const pending = pendingNewPasswordRef.current;
      if (!pending?.cognitoUser) {
        resolve({
          success: false,
          error: "Session expired. Sign in again with your temporary password.",
        });
        return;
      }
      const { cognitoUser, userAttributes } = pending;
      cognitoUser.completeNewPasswordChallenge(
        newPassword,
        userAttributes,
        {
          onSuccess: (session) => {
            pendingNewPasswordRef.current = null;
            setUser(userFromSession(session));
            setIsAuthenticated(true);
            resolve({ success: true });
          },
          onFailure: (err) => {
            resolve({
              success: false,
              error: err.message || "Could not set new password",
            });
          },
        },
      );
    });
  }, []);

  const cancelNewPasswordChallenge = useCallback(() => {
    pendingNewPasswordRef.current = null;
  }, []);

  const forgotPassword = useCallback((email) => {
    return new Promise((resolve) => {
      if (isLocalDevBypassEnabled()) {
        resolve({
          success: false,
          error: "Password reset is not available in local demo mode.",
        });
        return;
      }
      if (!cognitoUserPool) {
        resolve({
          success: false,
          error: "Cognito is not configured.",
        });
        return;
      }
      const e = email.trim().toLowerCase();
      if (!e) {
        resolve({ success: false, error: "Enter your email address." });
        return;
      }
      createCognitoUser(e).forgotPassword({
        onSuccess: () => {
          resolve({ success: true });
        },
        onFailure: (err) => {
          resolve({
            success: false,
            error: err.message || "Could not send reset code",
          });
        },
      });
    });
  }, []);

  const confirmForgotPassword = useCallback((email, code, newPassword) => {
    return new Promise((resolve) => {
      if (!cognitoUserPool) {
        resolve({
          success: false,
          error: "Cognito is not configured.",
        });
        return;
      }
      const e = email.trim().toLowerCase();
      const verificationCode = code.trim();
      if (!verificationCode) {
        resolve({ success: false, error: "Enter the verification code from your email." });
        return;
      }
      createCognitoUser(e).confirmPassword(verificationCode, newPassword, {
        onSuccess: () => {
          resolve({ success: true });
        },
        onFailure: (err) => {
          resolve({
            success: false,
            error: err.message || "Could not reset password",
          });
        },
      });
    });
  }, []);

  /** Reserved for SIGNUP_ENABLED on Login; production uses admin invite only. */
  const signUp = useCallback((email, password) => {
    return new Promise((resolve) => {
      if (!cognitoUserPool) {
        resolve({
          success: false,
          error: "Cognito is not configured.",
        });
        return;
      }
      const e = email.trim().toLowerCase();
      cognitoUserPool.signUp(
        e,
        password,
        [new CognitoUserAttribute({ Name: "email", Value: e })],
        null,
        (err, result) => {
          if (err) {
            resolve({ success: false, error: err.message || "Sign up failed" });
            return;
          }
          resolve({
            success: true,
            userConfirmed: result.userConfirmed,
          });
        },
      );
    });
  }, []);

  const logout = useCallback(() => {
    pendingNewPasswordRef.current = null;
    const u = cognitoUserPool?.getCurrentUser();
    if (u) {
      u.signOut();
    }
    clearAuth();
  }, [clearAuth]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        groups,
        role,
        isAdmin,
        loading,
        login,
        signUp,
        forgotPassword,
        confirmForgotPassword,
        completeNewPassword,
        cancelNewPasswordChallenge,
        logout,
        isCognitoConfigured,
        isLocalDevBypassEnabled,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
